// ─── Mensajes del render ────────────────────────────────────────────────────
// El backend (`render-video-status`) devuelve labels pensados para debugging:
// "Renderizando en AWS…", "Inicializando renderer…", "Codificando video…".
// Nadie fuera del equipo sabe qué es AWS ni un renderer, así que la UI NO
// muestra ese texto: lo traducimos acá a frases con onda derivadas del
// PROGRESO, que es el único dato que el usuario necesita.
//
// Efecto lateral bueno: si el backend cambia sus labels (o suma uno nuevo),
// no se filtra jerga a la interfaz — este módulo es la única fuente de verdad
// de lo que se lee durante un export.

/** Etapas por rango de progreso. `until` es el techo (exclusivo) del tramo. */
const STAGES: Array<{ until: number; messages: string[] }> = [
  {
    until: 0.15,
    messages: ["Calentando motores…", "Juntando todas las piezas…"],
  },
  {
    until: 0.45,
    messages: [
      "Poniendo los pixeles en fila…",
      "Convenciendo a los frames de portarse bien…",
    ],
  },
  {
    until: 0.75,
    messages: [
      "Dibujando cuadro por cuadro…",
      "Esto es lo que tarda, prometemos que vale la pena…",
    ],
  },
  {
    until: 0.92,
    messages: ["Puliendo los últimos detalles…", "Dándole el toque final…"],
  },
  {
    until: Number.POSITIVE_INFINITY,
    messages: ["Ya casi, no cierres…", "Últimos segundos…"],
  },
];

/** Cada cuánto rota la frase dentro de una misma etapa. */
export const RENDER_MESSAGE_ROTATE_MS = 4000;

/**
 * Frase a mostrar bajo la barra de progreso.
 *
 * @param progress  0..1. Valores fuera de rango se recortan.
 * @param tick      Contador que avanza con el tiempo (ver
 *                  `RENDER_MESSAGE_ROTATE_MS`). Hace rotar la frase dentro de
 *                  la etapa para que no parezca trabado cuando el backend
 *                  tarda en actualizar el progreso.
 */
export function renderMessageFor(progress: number, tick = 0): string {
  const p = Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 1) : 0;
  if (p >= 1) return "¡Listo!";
  const stage = STAGES.find((s) => p < s.until) ?? STAGES[STAGES.length - 1];
  const safeTick = Number.isFinite(tick) ? Math.max(0, Math.floor(tick)) : 0;
  return stage.messages[safeTick % stage.messages.length];
}

// ─── Errores del export ─────────────────────────────────────────────────────
// Cuando un render falla, lo que llega es el texto crudo de Remotion/AWS:
// stacktraces con links a CloudWatch, "Rate Exceeded", "UnrecognizedClient…".
// Eso se le mostraba TAL CUAL al usuario en el panel de exportar.
//
// Acá se traduce a una frase que dice qué pasó y qué puede hacer. Tres reglas
// que salieron de mirar los errores reales de `render_jobs`:
//
//  1. Si no reconocemos el error, NO se muestra el texto crudo: va un mensaje
//     genérico. El detalle técnico no se pierde — queda en
//     `render_jobs.error_message`, que es donde el equipo lo mira.
//  2. Los errores que son culpa NUESTRA (credenciales, configuración) no le
//     piden al usuario que haga algo que no puede hacer: le avisan que es un
//     problema nuestro.
//  3. Esta función es SOLO para los errores del render. Los del preflight
//     (`preflightExport`) ya vienen escritos para el usuario y no pasan por
//     acá — traducirlos los empeoraría.

/** Patrón técnico → qué le decimos al usuario. El orden importa: gana el primero. */
const ERRORES_CONOCIDOS: Array<{ patron: RegExp; mensaje: string }> = [
  {
    // Cuota de Lambda: hoy entra UN export a la vez en toda la plataforma.
    patron: /concurrency limit|rate exceeded|too many requests|throttl/i,
    mensaje:
      'Hay otro video exportándose en este momento. Esperá a que termine y probá de nuevo en un par de minutos.',
  },
  {
    // Un `delayRender` de una carga que nunca terminó (un video, una imagen o
    // un audio que no se pudo bajar). Va ANTES del timeout de la función: su
    // texto termina con el link "remotion.dev/docs/timeout" y caía en ese
    // patrón, y el usuario leía "exportalo en partes más cortas" por un
    // archivo que faltaba (caso real 2026-09-28: el overlay de la transición
    // en un bucket que ya no existía).
    patron: /delayrender\(\).*(loading|<html5video>|<img>|<audio>|<video>)/i,
    mensaje:
      'No pudimos cargar uno de los archivos del video. Revisá que los clips estén completos y probá de nuevo.',
  },
  {
    // La función de Lambda se quedó sin tiempo (videos largos).
    patron: /timed out|timeout|task timed out/i,
    mensaje:
      'El video tardó demasiado y se canceló. Probá exportarlo en partes más cortas, o avisanos si se repite.',
  },
  {
    // Credenciales / permisos de AWS: problema NUESTRO, no del usuario.
    patron:
      /unrecognizedclient|credentials|accessdenied|not authorized to perform|invalid.*signature/i,
    mensaje:
      'Hay un problema de configuración en el servicio de exportación. Ya estamos al tanto — escribinos si necesitás el video con urgencia.',
  },
  {
    // Un asset del proyecto no se pudo bajar (URL vencida, archivo borrado).
    patron: /failed to fetch|could not (download|load)|enotfound|econnrefused|fetch failed/i,
    mensaje:
      'No pudimos cargar uno de los archivos del video. Revisá que los clips estén completos y probá de nuevo.',
  },
  {
    // Sin espacio en disco de la Lambda.
    patron: /no space left|disk (quota|full)|enospc/i,
    mensaje:
      'El video es demasiado pesado para exportarlo de una. Probá dividirlo en partes más cortas.',
  },
  {
    // La composición no se pudo montar (bug del renderer).
    patron: /composition|delayrender|cannot read propert|is not a function|undefined/i,
    mensaje:
      'Hubo un error al armar el video. Probá de nuevo; si se repite, avisanos qué clips tiene el proyecto.',
  },
];

/** Lo que ve el usuario cuando no reconocemos el error. */
const ERROR_GENERICO =
  'No pudimos exportar el video. Probá de nuevo en unos minutos; si vuelve a fallar, avisanos.';

/**
 * Traduce el error de un render a algo que una persona pueda entender y
 * accionar. Pensada para el `catch` del export, NUNCA para los mensajes del
 * preflight (ver la regla 3 de arriba).
 *
 * @param crudo  El error tal como vino de Remotion / AWS / la Edge Function.
 */
export function mensajeDeErrorDeExport(crudo: unknown): string {
  const texto =
    crudo instanceof Error
      ? crudo.message
      : typeof crudo === 'string'
        ? crudo
        : '';
  if (!texto.trim()) return ERROR_GENERICO;

  // Cancelar no es un error: es algo que el usuario pidió. Se distingue para
  // que el panel no le muestre un cartel rojo por haber apretado "Cancelar".
  if (/cancelad|cancelled|canceled/i.test(texto)) {
    return 'Cancelaste la exportación.';
  }

  const conocido = ERRORES_CONOCIDOS.find((e) => e.patron.test(texto));
  return conocido ? conocido.mensaje : ERROR_GENERICO;
}
